import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll, { Props as InfiniteScrollProps } from 'react-infinite-scroll-component';
import { BusySpinner } from './overlay/components/Busy';

interface InfiniteScrollComponentProps extends Partial<InfiniteScrollProps> {
    /** Array of items (used to track dataLength) */
    items?: any[];
    /** Whether there are more items to load */
    hasMore: boolean;
    /** Function to call when more data is needed */
    fetchMore: () => void;
    /** CSS class name of the scrollable container (optional) */
    scrollableTargetClass?: string | null;
}

const InfiniteScrollComponent: React.FC<InfiniteScrollComponentProps> = ({
    items = [],
    hasMore,
    fetchMore,
    children,
    scrollableTargetClass = null,
    scrollableTarget = null,
    loader = <BusySpinner />,
    ...restProps
}) => {
    const scrollElementRef = useRef<HTMLElement | null>(null);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        if (scrollableTargetClass) {
            // Try multiple times to find the element
            let attempts = 0;
            const maxAttempts = 10;
            
            const findElement = () => {
                const element = document.querySelector(`.${scrollableTargetClass}`) as HTMLElement;
                if (element) {
                    scrollElementRef.current = element;
                    setIsReady(true);
                } else if (attempts < maxAttempts) {
                    attempts++;
                    setTimeout(findElement, 50);
                } else {
                    setIsReady(true); // Proceed anyway
                }
            };
            
            findElement();
        } else {
            setIsReady(true);
        }
    }, [scrollableTargetClass]);

    const handleFetchMore = () => {
        fetchMore();
    };

    if (!isReady) {
        return <>{children}</>;
    }

    return (
        <InfiniteScroll
            dataLength={items.length}
            next={handleFetchMore}
            hasMore={hasMore}
            loader={loader}
            scrollableTarget={(scrollElementRef.current || scrollableTarget || undefined) as any}
            {...restProps}
        >
            {children}
        </InfiniteScroll>
    );
};

export default InfiniteScrollComponent;
